import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Clock, Bell, Shield, Users } from 'lucide-react';
import SEO from '@/components/SEO';
import { useAuth } from '@/contexts/AuthContext';
import FirmInfoSection from '@/components/admin/settings/FirmInfoSection';
import BusinessHoursSection from '@/components/admin/settings/BusinessHoursSection';
import NotificationsSection from '@/components/admin/settings/NotificationsSection';
import SecuritySection from '@/components/admin/settings/SecuritySection';
import TeamManagementSection from '@/components/admin/settings/TeamManagementSection';

const settingsTabs = [
  {
    value: 'firm',
    label: 'Firm Info',
    icon: Building2,
    component: FirmInfoSection,
  },
  {
    value: 'hours',
    label: 'Business Hours',
    icon: Clock,
    component: BusinessHoursSection,
  },
  {
    value: 'notifications',
    label: 'Notifications',
    icon: Bell,
    component: NotificationsSection,
  },
  {
    value: 'security',
    label: 'Security',
    icon: Shield,
    component: SecuritySection,
  },
  {
    value: 'team',
    label: 'Team',
    icon: Users,
    component: TeamManagementSection,
    requiresSuperAdmin: true,
  },
];

const AdminSettings = () => {
  const { role } = useAuth();
  const [activeTab, setActiveTab] = useState('firm');

  // Filter tabs based on role
  const availableTabs = settingsTabs.filter(tab => {
    if (tab.requiresSuperAdmin && role !== 'superadmin') return false;
    return true;
  });

  return (
    <>
      <SEO 
        title="Settings - Admin"
        description="Configure admin panel settings"
      />
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Configure system settings and preferences
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex items-center gap-2 data-[state=active]:bg-background"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {availableTabs.map((tab) => {
            const Component = tab.component;
            return (
              <TabsContent key={tab.value} value={tab.value} className="mt-6">
                <Component />
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </>
  );
};

export default AdminSettings;
